import React from 'react';
import styled, { css } from 'styled-components';

/**
 * The one place where the receipt block talks back to the client.
 *
 * Every screen used to print these as one more line of small grey-or-red text, set in the same
 * font and size as the instruction right above it. On a phone that line landed a few pixels under
 * the upload button and read as part of the caption — so «Файл пустой, выберите другой» looked
 * like more advice about what to attach, not like the reason nothing had been sent. A tinted box
 * with a border and its own breathing room is what makes it an answer to the tap.
 *
 * Two tones, and the difference is whether the client has something to do:
 * `error` — the file or the request was refused, he has to pick another file or try again;
 * `neutral` — we are still working (a retry in flight) or offering help, nothing is broken.
 */
export type UploadNoticeTone = 'error' | 'neutral';

const ERROR_FG = '#b8291c';
const NEUTRAL_FG = '#6f6f6f';

const Box = styled.div<{ $tone: UploadNoticeTone }>`
  /* The gap is the point on mobile: the button is a big tap target and the message used to sit
     8-10px from it, close enough to read as its caption. Symmetric because the screens disagree
     about the side — the payment screen puts the block under the button, the re-upload screens
     above it. */
  margin: 14px 0;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid;
  font-family: 'PT Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
  text-align: left;
  /* Long file names and locale strings must wrap, not stretch the card. */
  overflow-wrap: anywhere;

  ${({ $tone }) =>
    $tone === 'error'
      ? css`
          color: ${ERROR_FG};
          background: rgba(214, 50, 36, 0.07);
          border-color: rgba(214, 50, 36, 0.28);
        `
      : css`
          color: ${NEUTRAL_FG};
          background: rgba(0, 0, 0, 0.035);
          border-color: rgba(0, 0, 0, 0.1);
        `}

  @media (max-width: 575px) {
    margin: 16px 0;
    font-size: 13px;
  }
`;

interface Props {
  tone: UploadNoticeTone;
  children: React.ReactNode;
  /** Screens that place the block above the button need the spacing on the other side. */
  className?: string;
}

const UploadNotice = ({ tone, children, className }: Props) => (
  <Box
    $tone={tone}
    className={className}
    role={tone === 'error' ? 'alert' : 'status'}
    data-sentry-element={tone === 'error' ? 'upload-notice-error' : 'upload-notice-neutral'}
  >
    {children}
  </Box>
);

export default UploadNotice;
